import type { ReactNode } from 'react'

type CardProps = {
  title?: string
  description?: string
  children: ReactNode
  className?: string
  actions?: ReactNode
}

export function Card({
  title,
  description,
  children,
  className = '',
  actions,
}: CardProps) {
  return (
    <div className={`card ${className}`}>
      {(title ?? description ?? actions) && (
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              {title && (
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {title}
                </h3>
              )}
              {description && (
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {description}
                </p>
              )}
            </div>
            {actions && <div className="flex-shrink-0">{actions}</div>}
          </div>
        </div>
      )}
      <div className="px-4 py-5 sm:p-6">{children}</div>
    </div>
  )
}

type CardStatProps = {
  title: string
  value: string | number
  change?: string
  changeType?: 'positive' | 'negative' | 'neutral'
  icon?: ReactNode
}

export function StatCard({
  title,
  value,
  change,
  changeType = 'neutral',
  icon,
}: CardStatProps) {
  const changeColors = {
    positive: 'text-success-600',
    negative: 'text-danger-600',
    neutral: 'text-gray-500',
  }

  return (
    <div className="card px-4 py-5 sm:p-6">
      <div className="flex items-center">
        {icon && (
          <div className="flex-shrink-0 rounded-md bg-primary-100 p-3 mr-4">
            {icon}
          </div>
        )}
        <div className="flex-1">
          <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
          <dd className="mt-1 flex items-baseline">
            <span className="text-2xl font-semibold text-gray-900">{value}</span>
            {change && (
              <span
                className={`ml-2 text-sm font-medium ${changeColors[changeType]}`}
              >
                {change}
              </span>
            )}
          </dd>
        </div>
      </div>
    </div>
  )
}
